import React, { useEffect, useState } from "react";
import { StatusOptions } from "@/utils/constants/constants";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import TableHead from "@/components/tables/TableHead";
import { fetchUserEnvos } from "@/services/envelope.service";
import ReportedEnvelopeTable from "@/components/tables/ReportedEnvelopeTable";
import {
    getListCacheBootstrap,
    invalidateListCacheByPage,
    writeListCache,
} from "@/utils/listPageCache";

interface ReportedUserEnvelopeTableProps {
    id: string;
}

const BASE_ENVELOPE_FILTER = {
    is_reported: true,
    get_created_by: true,
};

const isEmptyFilter = (value: Record<string, unknown>) =>
    Object.keys(value).length === 0;

const mergeEnvelopeFilter = (userFilter: Record<string, any> = {}) => ({
    ...userFilter,
    ...BASE_ENVELOPE_FILTER,
});

const ReportedUserEnvelopeTable = (props: ReportedUserEnvelopeTableProps) => {
    const [
        pageNo,
        setPageNo,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();

    const [appliedFilter, setAppliedFilter] = useState(filter);

    const envelopeFilter = mergeEnvelopeFilter(appliedFilter);
    const cachePageId = `reported-user-envelopes-${props.id}`;

    const listBootstrap = getListCacheBootstrap(cachePageId, {
        dataLength,
        sorting,
        searchItem,
        filter: envelopeFilter,
    });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [envelopes, setEnvelopes] = useState<any[]>(listBootstrap.items);
    const [envelopesLeft, setEnvelopesLeft] = useState(true);
    const [envelopesHistory, setEnvelopesHistory] = useState<any[]>(
        listBootstrap.items
    );
    const [allFetched, setAllFetched] = useState(false);
    const [loadData, setLoadData] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap(cachePageId, {
            dataLength,
            sorting,
            searchItem,
            filter: envelopeFilter,
        });

        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);

        if (cached && isEmptyFilter(appliedFilter)) {
            setEnvelopes(cached.items);
            setEnvelopesHistory(cached.items);
            setTotalCount(cached.totalCount);
            setLoadData(false);
            setIsLoading(false);
            return;
        }

        setEnvelopes([]);
        setEnvelopesHistory([]);
        setIsLoading(true);
        setLoadData(true);

        fetchUserEnvos(
            props.id,
            1,
            dataLength,
            sorting,
            envelopeFilter,
            searchItem,
            (response: any) => {
                const nextEnvelopes = response.data.envelopes ?? [];
                writeListCache(
                    cacheKey,
                    nextEnvelopes,
                    response.data.total_count
                );
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (nextEnvelopes.length < dataLength) {
                    setEnvelopesLeft(false);
                    setAllFetched(true);
                }
                setEnvelopes(nextEnvelopes);
                setEnvelopesHistory(nextEnvelopes);
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
            }
        );
    }, [props.id, dataLength, sorting, searchItem, appliedFilter]);

    useEffect(() => {
        const handleScroll = () => {
            if (allFetched || isLoading || !envelopesLeft) {
                return;
            }

            const isAtBottom =
                window.innerHeight +
                    document.documentElement.scrollTop >=
                document.documentElement.offsetHeight - 1;

            if (!isAtBottom) {
                return;
            }

            const nextPage = pageNo + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchUserEnvos(
                props.id,
                nextPage,
                dataLength,
                sorting,
                envelopeFilter,
                searchItem,
                (response: any) => {
                    setIsLoading(false);
                    setTotalCount(response.data.total_count);
                    if (response.data?.envelopes?.length < dataLength) {
                        setEnvelopesLeft(false);
                        setAllFetched(true);
                    }
                    const nextEnvelopes = response.data.envelopes ?? [];
                    setEnvelopes((prev) => [...prev, ...nextEnvelopes]);
                    setEnvelopesHistory((prev) => [...prev, ...nextEnvelopes]);
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                }
            );
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [
        allFetched,
        isLoading,
        envelopesLeft,
        pageNo,
        dataLength,
        sorting,
        appliedFilter,
        searchItem,
        props.id,
    ]);

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const reset = () => {
        invalidateListCacheByPage(cachePageId);
        resetDataQueries?.();
        setAppliedFilter({});
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setLoadData(true);
        setIsLoading(true);
    };

    const handleApplyFilter = (filterOverride?: Record<string, any>) => {
        const nextFilter = filterOverride ?? filter;

        invalidateListCacheByPage(cachePageId);
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setLoadData(true);
        setIsLoading(true);
        setAppliedFilter(nextFilter);
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="reported-user-envelope-table"
                    handleReset={reset}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    reset={reset}
                    search={search}
                    title="Envelopes"
                    options={StatusOptions}
                    handleApplyFilter={handleApplyFilter}
                    currentEnvo={envelopes.length}
                    totalEnvo={totalCount}
                />
            </div>
            <div>
                <ReportedEnvelopeTable
                    id="reported-user-envelope-table"
                    reset={reset}
                    title="Envelopes"
                    setEnvelopes={setEnvelopes}
                    search={search}
                    isLoading={isLoading}
                    dataLength={dataLength as number}
                    sorting={sorting}
                    setSorting={setSorting as (sorting: any) => void}
                    searchItem={searchItem as string}
                    allFetched={allFetched}
                    envelopes={envelopes}
                    dataLoad={loadData}
                    resetDataQueries={resetDataQueries}
                    setPageNo={setPageNo}
                />
            </div>
        </div>
    );
};

export default ReportedUserEnvelopeTable;
